import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import CommonTextBox from "../../components/commonFields/commonTextBox";
import CommonButton from "../../components/commonFields/CommonButton";
import ConfirmationModal from "../../components/commonModals/ConfirmationModal";
import CommonSelect from "../../components/commonFields/CommonSelect";
import sheetValidationSchema from "../../validationSchemas/sheetInformationValidations";
import toast from "../../utils/toastsMessage";

import {
  deleteSheet,
  getSheetInfo,
  getSheetWeightInfo,
  updateSheet,
} from "../../services/sheet";
import {
  ISheetWeightDataRes,
  SheetData,
  SheetInfoRes,
} from "../../interfaces/commonInterface";
import { MEMBERSHIP_PLANS_TYPE } from "../../constant/commonConstants";
import warning from "../../assets/images/warning.svg";
import SVGFiles from "../../components/SVGFiles";

const SheetInformation: React.FC = () => {
  const { t: translation } = useTranslation();
  const bottomRef = React.useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(sheetValidationSchema(translation)),
  });

  const { fields, remove, append } = useFieldArray({
    name: "sheetInformation",
    control,
  });
  const [showConfirmationModal, setShowConfirmationModal] =useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [sheetId, setSheetId] = useState<number>();
  const [sheetIndex, setSheetIndex] = useState(0);

  const handleSheetInformation = async () => {
    if (sheetId) {
      setLoading(true);

      const deleteRes = await deleteSheet({ id: sheetId });
      if (deleteRes?.data?.success) {
        toast.success(translation(deleteRes?.data?.message));
        setShowConfirmationModal(false);
        remove(sheetIndex);
      } else toast.error(translation(deleteRes?.data?.message));
    } else {
      setShowConfirmationModal(false);
      remove(sheetIndex);
    }

    setLoading(false);
  };

  const onSubmit = async (data: SheetData[]) => {
    setSubmitLoading(true);
    const res = await updateSheet(data);
    if (res?.data?.success) toast.success(translation(res?.data?.message));
    else toast.error(translation(res?.data?.message));
    setSubmitLoading(false);
  };

  function checkWeight(info: ISheetWeightDataRes[], sheet: SheetInfoRes) {
    let weight = 0;
    info.forEach((item: ISheetWeightDataRes) => {
      if (
        +item.size.split(" ")[0] === sheet.length &&
        +item.size.split(" ")[3] === sheet.width
      )
        weight = item.sheet_weight;
    });
    return weight;
  }

  const getSheetList = async () => {
    setLoading(true);
    const sheetres = await getSheetWeightInfo();

    const res = await getSheetInfo();
    if (res?.data?.data?.length > 0) {
      res?.data?.data?.forEach((sheet: SheetInfoRes) => {
        let sheetWeight = 0;
        if (sheetres?.data?.success) {
          sheetWeight = checkWeight(sheetres?.data?.data, sheet);
        }

        append({
          sheetLength: sheet.length,
          sheetWidth: sheet.width,
          minQuantity: sheet.min_quantity,
          maxQuantity: sheet.max_quantity,
          sheetPrice: sheet.price_per_sheet,
          sheetId: sheet.id,
          membershipType: sheet.membership_type,
          discount: sheet.discount,
          weight: sheetWeight > 0 ? sheetWeight : undefined,
        });
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    getSheetList();
  }, []);

  return (
    <>
      <div className="container-fluid">
        <div className="p-4">
          <div className="white-card mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <h1>{translation("sheet_list")}</h1>
              <CommonButton
                className="theme-button primary-btn me-0 ms-0"
                type="button"
                onClick={() => {
                  append({
                    sheetLength: undefined,
                    sheetWidth: undefined,
                    minQuantity: undefined,
                    maxQuantity: undefined,
                    sheetPrice: undefined,
                    sheetId: undefined,
                    membershipType: undefined,
                    discount: undefined,
                  });
                  setTimeout(() => {
                    if (bottomRef.current) {
                      bottomRef.current.scrollIntoView({ behavior: "smooth" });
                    }
                  }, 100);
                }}
              >
                {translation("add_new")}
              </CommonButton>
            </div>
          </div>

          <div className="white-card">
            <form
              onSubmit={handleSubmit((data) =>
                onSubmit(data.sheetInformation as SheetData[])
              )}
              noValidate
            >
              <div>
                {fields.length > 0 ? (
                  (fields as SheetData[]).map((item, index: number) => {
                    return (
                      <div className="row mb-2">
                        <div className="col-lg-6">
                          {" "}
                          <input
                            type="hidden"
                            {...register(`sheetInformation[${index}].sheetId`)}
                            value={item.sheetId ?? ""}
                          />
                          <div className="form-group w-100">
                            <CommonTextBox
                              control={control}
                              label={translation("sheet_length_dim")}
                              name={`sheetInformation[${index}].sheetLength`}
                              type="number"
                              className="form-control"
                              error={
                                errors?.sheetInformation &&
                                errors?.sheetInformation[index] &&
                                errors?.sheetInformation[index].sheetLength
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          {" "}
                          <div className="form-group w-100">
                            <CommonTextBox
                              control={control}
                              label={translation("sheet_width_dim")}
                              name={`sheetInformation[${index}].sheetWidth`}
                              type="number"
                              className="form-control"
                              error={
                                errors?.sheetInformation &&
                                errors?.sheetInformation[index] &&
                                errors?.sheetInformation[index].sheetWidth
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          {" "}
                          <div className="form-group w-100">
                            <CommonTextBox
                              control={control}
                              label={translation("min_quantity")}
                              name={`sheetInformation[${index}].minQuantity`}
                              type="number"
                              className="form-control"
                              error={
                                errors?.sheetInformation &&
                                errors?.sheetInformation[index] &&
                                errors?.sheetInformation[index].minQuantity
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          {" "}
                          <div className="form-group w-100">
                            <CommonTextBox
                              control={control}
                              label={translation("max_quantity")}
                              name={`sheetInformation[${index}].maxQuantity`}
                              type="number"
                              className="form-control"
                              error={
                                errors?.sheetInformation &&
                                errors?.sheetInformation[index] &&
                                errors?.sheetInformation[index].maxQuantity
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          {" "}
                          <div className="form-group w-100">
                            <CommonTextBox
                              control={control}
                              label={translation("sheet_price")}
                              name={`sheetInformation[${index}].sheetPrice`}
                              type="number"
                              className="form-control"
                              error={
                                errors?.sheetInformation &&
                                errors?.sheetInformation[index] &&
                                errors?.sheetInformation[index].sheetPrice
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          {" "}
                          <div className="form-group w-100">
                            <div className="form-group">
                              <CommonSelect
                                className="form-control"
                                label={translation("membership_ship")}
                                option={MEMBERSHIP_PLANS_TYPE}
                                name={`sheetInformation[${index}].membershipType`}
                                errors={
                                  errors?.sheetInformation &&
                                  errors?.sheetInformation[index] &&
                                  errors?.sheetInformation[index].membershipType
                                }
                                control={control}
                                firstOption={translation(
                                  "select_memebership_type"
                                )}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-6">
                          {" "}
                          <div className="form-group w-100">
                            <CommonTextBox
                              control={control}
                              label={translation("sheet_discount")}
                              name={`sheetInformation[${index}].discount`}
                              type="number"
                              className="form-control"
                              error={
                                errors?.sheetInformation &&
                                errors?.sheetInformation[index] &&
                                errors?.sheetInformation[index].discount
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form-group w-100">
                            <div className="double-icon-input">
                              <CommonTextBox
                                control={control}
                                disabled
                                label={translation("sheet_weight_lb")}
                                name={`sheetInformation[${index}].weight`}
                                type="text"
                                className="form-control"
                              />
                              {!item.weight ? (
                                <span className="right-icon right-icon-custom custom-tooltip">
                                  <img src={warning} alt="" />
                                  <p className="custom-tooltip-content custom-tooltip-content-custom">
                                    {translation("sheet_weight_error")}
                                  </p>
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6 text-center mb-4">
                          {fields.length > 0 && (
                            <button
                              type="button"
                              className="icon-btn primary-btn sheet-list-icon"
                              onClick={() => {
                                setSheetId(item.sheetId);
                                setSheetIndex(index);
                                setShowConfirmationModal(true);
                              }}
                              disabled={submitLoading}
                            >
                              <SVGFiles name="transhIcon" />
                            </button>
                          )}
                        </div>
                        <hr />
                      </div>
                    );
                  })
                ) : loading ? (
                  <h3 className="text-center mb-3">{translation("loading")}</h3>
                ) : (
                  !loading && <p>{translation("no_data")}</p>
                )}
              </div>
              <div ref={bottomRef} />
              <div className="text-center pb-4">
                <CommonButton
                  className="theme-button primary-btn me-0 ms-0"
                  type="submit"
                  disabled={!fields.length || submitLoading}
                >
                  {translation("submit_button")}
                  {submitLoading && (
                    <div className="spinner-border text-light" role="status" />
                  )}
                </CommonButton>
              </div>
            </form>
          </div>
        </div>
      </div>
      {showConfirmationModal && (
        <ConfirmationModal
          heading="delete_sheet"
          description="active_deactive"
          onClickOkay={() => handleSheetInformation()}
          onClickCancel={() => setShowConfirmationModal(false)}
          disabled={loading}
        />
      )}
    </>
  );
};
export default SheetInformation;
